// nono-carousel.js: <nono-carousel> rails (Content/Review carousel 1016:11413, member films, machine rails).
// Native horizontal scroll with scroll-snap does the swiping; this module adds the arrows (one card or one page
// per press, no wrap-around, aria-disabled at the ends, focus stays on the button), arrow keys / Home / End on the
// focused track, the «1–3 av 13» counter and the dots. Never autoplays. FERDIG 02 §10 / §13, Figma 927:5937, 129:3389.
// Optional `loop` attribute: the arrows and arrow keys wrap (last → first, first → last), so they are never
// aria-disabled; Home / End and the counter are unchanged.

const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

// Deferred slides (c-image-slot loading: 'defer', speed 21.10.2026): their images carry data-src / data-srcset /
// data-sizes and fetch nothing until hydrated. The slide in view hydrates at once, the next one when the page has
// settled (load + idle), all of them on the first touch, scroll, arrow or key in the carousel.
function hydrate(root) {
  root.querySelectorAll('[data-defer], source[data-srcset]').forEach((el) => {
    const pic = el.closest('picture') || el;
    pic.querySelectorAll('source[data-srcset]').forEach((s) => {
      if (s.dataset.sizes) s.sizes = s.dataset.sizes;
      s.srcset = s.dataset.srcset;
      s.removeAttribute('data-srcset');
    });
    const img = pic.querySelector('img[data-defer]') || (el.matches('img[data-defer]') ? el : null);
    if (!img) return;
    if (img.dataset.sizes) img.sizes = img.dataset.sizes;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    if (img.dataset.src) img.src = img.dataset.src;
    ['src', 'srcset', 'sizes'].forEach((k) => img.removeAttribute(`data-${k}`));
    img.removeAttribute('data-defer');
  });
}
window.nonoHydrate = hydrate; // nono-provetime.js (gallery lightbox copies)
const settled = (fn) => {
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 300));
  if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
};

class NonoCarousel extends HTMLElement {
  connectedCallback() {
    if (this.track) return;
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.counter = this.querySelector('[data-carousel-counter]');
    this.dots = this.querySelector('[data-carousel-dots]');
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('keydown', (e) => this.key(e));
    let raf = 0;
    const later = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.update()); };
    this.track.addEventListener('scroll', later, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(later).observe(this.track);
    this.update();
    if (this.querySelector('[data-defer]')) {
      const items = this.items;
      const { first } = this.measure();
      if (items[first]) hydrate(items[first]);
      settled(() => items[first + 1] && hydrate(items[first + 1]));
      const all = () => {
        hydrate(this);
        ['pointerdown', 'keydown', 'focusin'].forEach((t) => this.removeEventListener(t, all));
        this.track.removeEventListener('scroll', all);
      };
      ['pointerdown', 'keydown', 'focusin'].forEach((t) => this.addEventListener(t, all, { passive: true }));
      this.track.addEventListener('scroll', all, { passive: true, once: true });
    }
  }

  get items() { return [...this.track.children]; }

  /** Index of the first item at or past the snap line, and how many fit fully. */
  measure() {
    const items = this.items;
    const pad = parseFloat(getComputedStyle(this.track).paddingInlineEnd) || 0;
    const box = this.track.getBoundingClientRect();
    const left = box.left - 1;
    const right = box.right - pad + 1;
    let first = -1;
    let last = -1;
    items.forEach((it, i) => {
      const r = it.getBoundingClientRect();
      if (r.left >= left && r.right <= right) { if (first < 0) first = i; last = i; }
    });
    if (first < 0) {
      first = Math.max(0, items.findIndex((it) => it.getBoundingClientRect().right > box.left + 1));
      last = first;
    }
    return { first, last, visible: last - first + 1, n: items.length };
  }

  get loop() { return this.hasAttribute('loop'); }

  go(dir) {
    const btn = dir < 0 ? this.prev : this.next;
    if (btn?.getAttribute('aria-disabled') === 'true') return;
    const { first, visible, n } = this.measure();
    if (this.loop && n > 1) {
      const t = this.track;
      if (dir > 0 && t.scrollLeft + t.clientWidth >= t.scrollWidth - 2) { this.to(0); return; }
      if (dir < 0 && t.scrollLeft <= 1) { this.to(n - 1); return; }
    }
    const by = this.dataset.step === 'page' ? visible : 1;
    const to = Math.max(0, Math.min(n - 1, first + dir * by));
    this.to(to);
  }

  to(i) {
    const it = this.items[i];
    if (!it) return;
    const delta = it.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollBy({ left: delta, behavior: smooth() });
  }

  key(e) {
    if (e.target !== this.track) return;
    const map = { ArrowLeft: -1, ArrowRight: 1 };
    if (e.key in map) { e.preventDefault(); this.go(map[e.key]); }
    else if (e.key === 'Home') { e.preventDefault(); this.to(0); }
    else if (e.key === 'End') { e.preventDefault(); this.to(this.items.length - 1); }
  }

  update() {
    const t = this.track;
    const atStart = t.scrollLeft <= 1;
    const atEnd = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
    const wrap = this.loop && this.items.length > 1;
    this.prev?.setAttribute('aria-disabled', String(atStart && !wrap));
    this.next?.setAttribute('aria-disabled', String(atEnd && !wrap));
    const { first, last, visible, n } = this.measure();
    const shownLast = atEnd ? n - 1 : last;
    const shownFirst = atEnd ? Math.max(0, n - visible) : first;
    if (this.counter) {
      const of = this.counter.dataset.of || 'av';
      const text = visible > 1 ? `${shownFirst + 1}–${shownLast + 1} ${of} ${n}` : `${shownFirst + 1} ${of} ${n}`;
      if (this.counter.textContent !== text) this.counter.textContent = text;
    }
    if (this.dots) {
      [...this.dots.children].forEach((d, i) => d.classList.toggle('is-active', i === shownFirst));
    }
  }
}

customElements.get('nono-carousel') || customElements.define('nono-carousel', NonoCarousel);

// Late images (c-image-slot data-late, speed 03.11.2026): lazy images below the first section. Each one is fetched
// when it comes within 1.5 screens of the viewport, and every one that is left once the page has settled, so the
// hero's poster gets the connection to itself while the page loads.
const late = [...document.querySelectorAll('img[data-late]')];
if (late.length) {
  const fetchImg = (img) => { img.removeAttribute('data-late'); hydrate(img.closest('picture') || img); };
  const io = window.IntersectionObserver
    ? new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { io.unobserve(e.target); fetchImg(e.target); }
    }), { rootMargin: '150% 0px' })
    : null;
  late.forEach((img) => (io ? io.observe(img) : fetchImg(img)));
  settled(() => late.forEach((img) => { if (img.hasAttribute('data-late')) { io?.unobserve(img); fetchImg(img); } }));
}
